/**
 * The ROUTE half of the fleet gate (TEC-07).
 *
 * Hiding a nav link is not an access control — `/ops/estate` is still typeable,
 * still bookmarked, and still linked from anything that was rendered before a
 * profile changed. Without this the deep link reaches `estate.ts`, which queries
 * `controlplane.*` on a box that must not have that schema, and the operator gets
 * PostgREST's schema-not-exposed error dressed as a product failure.
 *
 * A REFUSAL, not an error page: nothing is broken and nothing is missing. This
 * deployment is not the control plane, which is the whole point of a sovereign
 * box, so it says that plainly rather than inviting a support ticket.
 */
import type { FunctionComponent } from 'react'
import { EmptyState } from '@solum/ui'
import { consoleProfile, showsFleetSurfaces, FLEET_UNAVAILABLE } from '../data/profile'
import { useRegistry } from '../data/registry'

export function FleetGate({ children }: { children: React.ReactNode }) {
  const reg = useRegistry()
  if (!showsFleetSurfaces(consoleProfile(), (k) => reg.isInstalled(k))) {
    return (
      <div data-testid="fleet-unavailable">
        <EmptyState>{FLEET_UNAVAILABLE}</EmptyState>
      </div>
    )
  }
  return <>{children}</>
}

/** Wrap a route component so the gate applies wherever the route is reached from.
 *  FunctionComponent, not ComponentType — the router's `component` will not take
 *  a ComponentClass, and widening it here just moves the error to every call site. */
export function fleetGated<P extends object>(Component: FunctionComponent<P>): FunctionComponent<P> {
  const Gated: FunctionComponent<P> = (props) => <FleetGate><Component {...props} /></FleetGate>
  Gated.displayName = `fleetGated(${Component.displayName ?? Component.name ?? 'Component'})`
  return Gated
}
